import { useEffect, useState } from 'react'
import { Sparkles } from 'lucide-react'
import aiAdvisorService from '../../services/aiAdvisorService'

/**
 * Student opt-in toggle for the AI Advisor add-on.
 * Free for everyone today; copy notes it may become a paid add-on later.
 *
 * Mount this inside the settings page (e.g. the "Preferences" section of
 * SettingsPage.jsx), alongside CommunicationPreferences.
 */
export default function AIAdvisorSettings() {
  // Default OFF: the advisor is opt-in, so the toggle must not flash ON before getStatus() loads the
  // student's real opt-in state.
  const [enabled, setEnabled] = useState(false)
  const [available, setAvailable] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    aiAdvisorService
      .getStatus()
      .then((s) => {
        if (!active) return
        setEnabled(s.optedIn)
        setAvailable(s.featureEnabled)
      })
      .catch(() => setAvailable(false))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  const toggle = async () => {
    const next = !enabled
    setEnabled(next)
    setSaving(true)
    try {
      await aiAdvisorService.setEnabled(next)
    } catch {
      setEnabled(!next) // revert on failure
    } finally {
      setSaving(false)
    }
  }

  if (loading || !available) return null

  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/40">
          <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Advisor</h3>
          <p className="mt-1 max-w-prose text-sm text-gray-600 dark:text-gray-400">
            Get personalized, AI-generated insights on your progress: strengths, where you are stuck
            and why, and what to focus on next. Free while in preview; may become a paid add-on
            later.
          </p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={toggle}
        disabled={saving}
        className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
          enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}
